
import { CustomizableWatch } from '../../../mocks/customizable-watches';

interface ProductCustomizationPanelProps {
  watchProduct: CustomizableWatch;
  selectedOptions: Record<string, string>;
  onOptionChange: (category: string, value: string) => void;
  totalPrice: number;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onAddToCart: () => void;
}

const getCategoryLabel = (key: string): string => {
  const labels: Record<string, string> = {
    caseMaterial: 'Case Material',
    bezelType: 'Bezel Type',
    dialColor: 'Dial Color',
    hands: 'Hands Style',
    braceletStrap: 'Bracelet/Strap',
    movement: 'Movement',
    pushers: 'Pushers',
    subdialLayout: 'Subdial Layout',
    crownGuards: 'Crown Guards',
    caseShape: 'Case Shape',
    braceletIntegration: 'Bracelet',
    dayDisplay: 'Day Display',
    extras: 'Extras',
  };
  return labels[key] || key;
};

const getCategoryIcon = (key: string): string => {
  const icons: Record<string, string> = {
    caseMaterial: 'ri-box-3-line',
    bezelType: 'ri-circle-line',
    dialColor: 'ri-palette-line',
    hands: 'ri-time-line',
    braceletStrap: 'ri-links-line',
    movement: 'ri-settings-3-line',
    pushers: 'ri-radio-button-line',
    subdialLayout: 'ri-layout-grid-line',
    crownGuards: 'ri-shield-line',
    caseShape: 'ri-shape-line',
    braceletIntegration: 'ri-links-line',
    dayDisplay: 'ri-calendar-line',
    extras: 'ri-star-line',
  };
  return icons[key] || 'ri-settings-line';
};

const getOptionImage = (category: string, value: string, watchId: string): string => {
  const safeValue = value.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
  const safeCategory = category.toLowerCase().replace(/[^a-z0-9]/g, '-');
  const seq = `${watchId}-${safeCategory}-${safeValue}`;

  const prompts: Record<string, Record<string, string>> = {
    caseMaterial: {
      'Stainless Steel':
        'Polished stainless steel watch case close up on clean white background luxury timepiece component professional product photography showing brushed and polished finishing premium quality',
      Titanium:
        'Brushed titanium watch case close up on clean white background luxury timepiece component professional product photography showing lightweight matte grey finishing premium quality',
      'Two-Tone':
        'Two tone steel and gold watch case close up on clean white background luxury timepiece component professional product photography showing mixed metal finishing premium quality',
      'Yellow Gold':
        'Polished yellow gold watch case close up on clean white background luxury timepiece component professional product photography showing warm golden finishing premium quality',
      'Rose Gold':
        'Polished rose gold watch case close up on clean white background luxury timepiece component professional product photography showing warm pink gold finishing premium quality',
      'White Gold':
        'Polished white gold watch case close up on clean white background luxury timepiece component professional product photography showing cool silver white finishing premium quality',
      'Everose Gold':
        'Polished everose gold watch case close up on clean white background luxury timepiece component professional product photography showing warm rose gold finishing premium quality',
      Platinum:
        'Polished platinum watch case close up on clean white background luxury timepiece component professional product photography showing cool grey platinum finishing premium quality',
      Bronze:
        'Patina bronze watch case close up on clean white background luxury timepiece component professional product photography showing warm aged bronze finishing premium quality',
      'Black DLC':
        'Black DLC coated watch case close up on clean white background luxury timepiece component professional product photography showing matte black diamond like carbon finishing premium quality',
      'ADLC Black':
        'Black ADLC coated watch case close up on clean white background luxury timepiece component professional product photography showing deep black amorphous diamond coating premium quality',
      'Carbon Fiber':
        'Carbon fiber watch case close up on clean white background luxury timepiece component professional product photography showing woven carbon fiber pattern finishing premium quality',
      Ceramic:
        'Black ceramic watch case close up on clean white background luxury timepiece component professional product photography showing smooth scratch resistant ceramic finishing premium quality',
    },
    bezelType: {
      'Ceramic Black':
        'Black ceramic watch bezel insert close up on clean white background luxury timepiece component professional product photography showing glossy black ceramic with engraved markings',
      'Ceramic Blue':
        'Blue ceramic watch bezel insert close up on clean white background luxury timepiece component professional product photography showing deep blue ceramic with engraved markings',
      'Ceramic Green':
        'Green ceramic watch bezel insert close up on clean white background luxury timepiece component professional product photography showing deep green ceramic with engraved markings',
      'Ceramic Orange':
        'Orange ceramic watch bezel insert close up on clean white background luxury timepiece component professional product photography showing vibrant orange ceramic with engraved markings',
      'Aluminum Pepsi':
        'Red and blue pepsi aluminum watch bezel insert close up on clean white background luxury timepiece component professional product photography showing classic red blue color combination',
      'Aluminum Batman':
        'Black and blue batman aluminum watch bezel insert close up on clean white background luxury timepiece component professional product photography showing black blue color combination',
      'Aluminum Silver':
        'Silver aluminum watch bezel insert close up on clean white background luxury timepiece component professional product photography showing brushed silver aluminum with engraved markings',
      'Sapphire Insert':
        'Sapphire crystal watch bezel insert close up on clean white background luxury timepiece component professional product photography showing transparent sapphire with luminous markings',
      Smooth:
        'Smooth polished watch bezel close up on clean white background luxury timepiece component professional product photography showing clean polished smooth bezel finishing',
      Fluted:
        'Fluted watch bezel close up on clean white background luxury timepiece component professional product photography showing classic ridged fluted pattern finishing',
      Diamond:
        'Diamond set watch bezel close up on clean white background luxury timepiece component professional product photography showing brilliant cut diamonds set around bezel',
      'Engine-Turned':
        'Engine turned watch bezel close up on clean white background luxury timepiece component professional product photography showing fine crosshatch engraved pattern',
      'Baguette Diamond':
        'Baguette diamond watch bezel close up on clean white background luxury timepiece component professional product photography showing rectangular cut diamonds set around bezel',
    },
    dialColor: {
      Black:
        'Black watch dial face close up on clean white background luxury timepiece component professional product photography showing matte black dial with applied indices',
      Blue:
        'Blue sunburst watch dial face close up on clean white background luxury timepiece component professional product photography showing deep blue sunburst dial with applied indices',
      Green:
        'Green watch dial face close up on clean white background luxury timepiece component professional product photography showing deep green dial with applied indices',
      White:
        'White lacquer watch dial face close up on clean white background luxury timepiece component professional product photography showing clean white dial with applied indices',
      'Sunburst Silver':
        'Silver sunburst watch dial face close up on clean white background luxury timepiece component professional product photography showing radial silver sunburst dial with applied indices',
      'White Panda':
        'White panda chronograph watch dial face close up on clean white background luxury timepiece component professional product photography showing white dial with black subdials panda style',
      Silver:
        'Silver watch dial face close up on clean white background luxury timepiece component professional product photography showing brushed silver dial with applied indices',
      Carbon:
        'Carbon fiber pattern watch dial face close up on clean white background luxury timepiece component professional product photography showing woven carbon fiber textured dial',
      Orange:
        'Orange watch dial face close up on clean white background luxury timepiece component professional product photography showing vibrant orange dial with luminous indices',
      Champagne:
        'Champagne gold watch dial face close up on clean white background luxury timepiece component professional product photography showing warm champagne tone dial with applied indices',
      'Blue Sunburst':
        'Blue sunburst watch dial face close up on clean white background luxury timepiece component professional product photography showing radial blue sunburst pattern dial',
      Slate:
        'Slate grey watch dial face close up on clean white background luxury timepiece component professional product photography showing dark grey slate dial with applied indices',
      Wimbledon:
        'Wimbledon green and gold watch dial face close up on clean white background luxury timepiece component professional product photography showing green dial with gold roman numerals',
      Skeleton:
        'Skeleton openworked watch dial face close up on clean white background luxury timepiece component professional product photography showing exposed movement through skeletonized dial',
      'Roman Numerals':
        'Silver watch dial with roman numerals close up on clean white background luxury timepiece component professional product photography showing classic roman numeral indices',
      'Blue Horizontal':
        'Blue horizontal embossed watch dial face close up on clean white background luxury timepiece component professional product photography showing horizontal grooved blue dial pattern',
      'Black Horizontal':
        'Black horizontal embossed watch dial face close up on clean white background luxury timepiece component professional product photography showing horizontal grooved black dial pattern',
      'White Horizontal':
        'White horizontal embossed watch dial face close up on clean white background luxury timepiece component professional product photography showing horizontal grooved white dial pattern',
      'Brown Horizontal':
        'Brown horizontal embossed watch dial face close up on clean white background luxury timepiece component professional product photography showing horizontal grooved brown dial pattern',
      'Olive Green':
        'Olive green watch dial face close up on clean white background luxury timepiece component professional product photography showing deep olive green dial with applied indices',
      'Blue Tapisserie':
        'Blue tapisserie pattern watch dial face close up on clean white background luxury timepiece component professional product photography showing blue waffle textured tapisserie dial',
      'Black Tapisserie':
        'Black tapisserie pattern watch dial face close up on clean white background luxury timepiece component professional product photography showing black waffle textured tapisserie dial',
      'Silver Tapisserie':
        'Silver tapisserie pattern watch dial face close up on clean white background luxury timepiece component professional product photography showing silver waffle textured tapisserie dial',
      'Grey Tapisserie':
        'Grey tapisserie pattern watch dial face close up on clean white background luxury timepiece component professional product photography showing grey waffle textured tapisserie dial',
      Salmon:
        'Salmon pink watch dial face close up on clean white background luxury timepiece component professional product photography showing warm salmon pink dial with applied indices',
      'Green Ombre':
        'Green ombre gradient watch dial face close up on clean white background luxury timepiece component professional product photography showing dark to light green gradient dial',
      Chocolate:
        'Chocolate brown watch dial face close up on clean white background luxury timepiece component professional product photography showing rich chocolate brown dial with applied indices',
      'Ice Blue':
        'Ice blue watch dial face close up on clean white background luxury timepiece component professional product photography showing pale ice blue dial with diamond indices',
    },
  };

  const categoryPrompts = prompts[category];
  if (categoryPrompts && categoryPrompts[value]) {
    return `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28${categoryPrompts[value]}%29%7D&width=400&height=400&seq=${seq}&orientation=squarish`;
  }

  const genericPrompts: Record<string, string> = {
    caseMaterial: `${value} luxury watch case material close up on clean white background professional product photography showing premium finishing and craftsmanship detail high quality timepiece component`,
    bezelType: `${value} luxury watch bezel close up on clean white background professional product photography showing premium bezel design and finishing detail high quality timepiece component`,
    dialColor: `${value} luxury watch dial face close up on clean white background professional product photography showing premium dial design and finishing detail high quality timepiece component`,
    hands: `${value} style luxury watch hands close up on clean white background professional product photography showing premium hand design polished finishing high quality timepiece component`,
    braceletStrap: `${value} luxury watch bracelet strap close up on clean white background professional product photography showing premium bracelet design and finishing detail high quality timepiece component`,
    movement: `${value} luxury watch movement mechanism close up on clean white background professional product photography showing premium caliber design and finishing detail high quality timepiece component`,
    pushers: `${value} luxury chronograph watch pushers close up on clean white background professional product photography showing premium pusher design and finishing detail high quality timepiece component`,
    subdialLayout: `${value} luxury chronograph subdial layout close up on clean white background professional product photography showing premium subdial arrangement and finishing detail high quality timepiece component`,
    crownGuards: `${value} luxury watch crown guards close up on clean white background professional product photography showing premium crown guard design and finishing detail high quality timepiece component`,
    caseShape: `${value} luxury watch case shape close up on clean white background professional product photography showing premium case design proportions and finishing detail high quality timepiece component`,
    braceletIntegration: `${value} luxury watch integrated bracelet close up on clean white background professional product photography showing premium bracelet integration design and finishing detail high quality timepiece component`,
    dayDisplay: `${value} language day display window on luxury watch dial close up on clean white background professional product photography showing premium day wheel design high quality timepiece component`,
  };

  const prompt = genericPrompts[category] || `${value} luxury watch component close up on clean white background professional product photography premium quality`;
  return `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28${prompt}%29%7D&width=400&height=400&seq=${seq}&orientation=squarish`;
};

const getOptionPrice = (category: string, value: string): number => {
  const pricing: Record<string, Record<string, number>> = {
    caseMaterial: {
      'Stainless Steel': 0,
      Titanium: 300,
      'Two-Tone': 500,
      'Yellow Gold': 1500,
      'Rose Gold': 1500,
      'White Gold': 1500,
      'Everose Gold': 1500,
      Platinum: 2500,
      Bronze: 200,
      'Black DLC': 400,
      'ADLC Black': 400,
      'Carbon Fiber': 600,
      Ceramic: 800,
    },
    bezelType: {
      'Ceramic Black': 200,
      'Ceramic Blue': 200,
      'Ceramic Green': 200,
      'Ceramic Orange': 200,
      'Aluminum Pepsi': 0,
      'Aluminum Batman': 0,
      'Aluminum Silver': 0,
      'Sapphire Insert': 400,
      Smooth: 0,
      Fluted: 100,
      Diamond: 3000,
      'Engine-Turned': 150,
      'Baguette Diamond': 5000,
    },
    movement: {
      Automatic: 0,
      'GMT Automatic': 400,
      'High-Beat Automatic': 500,
      'Automatic Chronograph': 600,
      'Manual Chronograph': 400,
      'High-Frequency Chronograph': 500,
      'Automatic 300m': 0,
      'Automatic 500m': 200,
      'High-Beat 1000m': 500,
      'Automatic Date': 0,
      'Perpetual Automatic': 800,
      'COSC Certified': 300,
      'Skeleton Automatic': 900,
      'Manual Wind': 200,
      'Ultra-Thin Automatic': 700,
      'Automatic Power Reserve': 300,
      'Perpetual Calendar': 800,
      'Master Chronometer': 300,
    },
    braceletStrap: {
      'Oyster Bracelet': 0,
      'Jubilee Bracelet': 200,
      'Rubber Strap': 0,
      'NATO Strap': 0,
      'Leather Strap': 0,
      'Steel Bracelet': 0,
      'Racing Leather': 100,
      'Alligator Strap': 300,
      'Rubber Dive Strap': 0,
      'Mesh Bracelet': 100,
      'President Bracelet': 200,
    },
    braceletIntegration: {
      'Steel Integrated': 0,
      'Two-Tone Integrated': 400,
      'Leather Quick-Switch': 0,
      'Rubber Quick-Switch': 0,
      'Gold Integrated': 1200,
      'Rubber Strap': 0,
      'Leather Strap': 0,
    },
    pushers: {
      'Round Pushers': 0,
      'Rectangular Pushers': 0,
      'Screw-Down Pushers': 150,
    },
    subdialLayout: {
      'Tri-Compax': 100,
      'Bi-Compax': 0,
      'Mono-Pusher': 50,
    },
    hands: {
      Mercedes: 0,
      Sword: 0,
      Snowflake: 0,
      Dauphine: 0,
      Arrow: 0,
      Plongeur: 0,
      Baton: 0,
      Classic: 0,
      Modern: 0,
      Skeleton: 0,
      Roman: 0,
      Diamond: 500,
      'Sword Blue': 0,
      'Sword Black': 0,
      'Baton Luminous': 0,
      'Royal Oak Signature': 0,
    },
    dialColor: {
      Skeleton: 800,
    },
  };

  return pricing[category]?.[value] ?? 0;
};

export default function ProductCustomizationPanel({
  watchProduct,
  selectedOptions,
  onOptionChange,
  totalPrice,
  activeTab,
  setActiveTab,
  onAddToCart,
}: ProductCustomizationPanelProps) {
  const options = watchProduct.customizationOptions;
  const availableCategories = Object.entries(options).filter(([, values]) => values && values.length > 0);
  const allTabs = [...availableCategories.map(([key]) => key), 'extras'];

  const getProgressCount = () => {
    let count = 0;
    availableCategories.forEach(([key]) => {
      if (selectedOptions[key]) count++;
    });
    return count;
  };

  const totalSteps = availableCategories.length;
  const progressCount = getProgressCount();

  const isConfigurationComplete = () => {
    return availableCategories.every(([key]) => selectedOptions[key]);
  };

  const formatValue = (value: string): string => {
    return value.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <div className="space-y-6">
      {/* Visual Preview of Current Selection */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="font-serif text-xl font-semibold text-gray-900 mb-4">Your Selection</h3>
        <div className="w-full h-96 bg-gray-50 rounded-lg overflow-hidden border-2 border-gray-200">
          {activeTab && activeTab !== 'extras' && selectedOptions[activeTab] ? (
            <img
              src={getOptionImage(activeTab, selectedOptions[activeTab], watchProduct.id)}
              alt={`Selected ${getCategoryLabel(activeTab)}`}
              title={`Your current ${getCategoryLabel(activeTab)} selection`}
              className="w-full h-full object-cover object-center"
            />
          ) : (
            <img
              src={watchProduct.image}
              alt={watchProduct.name}
              title={watchProduct.name}
              className="w-full h-full object-cover object-center"
            />
          )}
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg font-semibold text-gray-900">Configuration Progress</h3>
          <span className="text-sm text-gray-600">
            {progressCount}/{totalSteps} Complete
          </span>
        </div>
        <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${totalSteps}, 1fr)` }}>
          {availableCategories.map(([key]) => (
            <div
              key={key}
              className={`h-2 rounded-full ${selectedOptions[key] ? 'bg-gray-900' : 'bg-gray-200'}`}
            ></div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gray-100 rounded-xl p-1 flex overflow-x-auto">
        {allTabs.map((tabKey) => (
          <button
            key={tabKey}
            onClick={() => setActiveTab(tabKey)}
            className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
              activeTab === tabKey
                ? 'bg-white text-gray-900 shadow-md'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center mx-auto mb-1">
              <i className={`${getCategoryIcon(tabKey)} text-lg`}></i>
            </div>
            {getCategoryLabel(tabKey)}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-lg p-6 min-h-[400px]">
        {availableCategories.map(([key, values]) => {
          if (activeTab !== key) return null;
          const useGrid = (values?.length || 0) <= 4;
          return (
            <div key={key}>
              <h3 className="font-serif text-xl font-semibold text-gray-900 mb-4">
                {getCategoryLabel(key)}
              </h3>
              <div className={useGrid ? 'grid grid-cols-1 gap-3' : 'grid grid-cols-1 gap-3'}>
                {values?.map((value) => {
                  const price = getOptionPrice(key, value);
                  return (
                    <button
                      key={value}
                      onClick={() => onOptionChange(key, value)}
                      className={`p-4 rounded-lg border-2 transition-all text-left cursor-pointer ${
                        selectedOptions[key] === value
                          ? 'border-gray-900 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-20 h-20 bg-gray-100 rounded-md overflow-hidden flex-shrink-0">
                          <img
                            src={getOptionImage(key, value, watchProduct.id)}
                            alt={value}
                            className="w-full h-full object-cover object-center"
                          />
                        </div>
                        <div className="flex-1 flex justify-between items-center">
                          <div>
                            <span className="font-medium text-gray-900 block">{value}</span>
                            {selectedOptions[key] === value && (
                              <span className="text-xs text-gray-500">Selected</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600">
                              {price > 0 ? `+$${price.toLocaleString()}` : 'Included'}
                            </span>
                            {selectedOptions[key] === value && (
                              <i className="ri-check-line text-xl text-gray-900"></i>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {activeTab === 'extras' && (
          <div>
            <h3 className="font-serif text-xl font-semibold text-gray-900 mb-4">Optional Add-ons</h3>
            <div className="space-y-4">
              <div className="p-4 border-2 border-gray-200 rounded-lg">
                <label className="block mb-2">
                  <span className="font-medium text-gray-900">Custom Engraving (+$50)</span>
                  <p className="text-sm text-gray-600 mt-1">Add a personal message (max 20 characters)</p>
                </label>
                <input
                  type="text"
                  maxLength={20}
                  value={selectedOptions.engraving || ''}
                  onChange={(e) => onOptionChange('engraving', e.target.value)}
                  placeholder="Enter your message"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm"
                />
              </div>

              <div className="p-4 border-2 border-gray-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-gift-line text-gray-900"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Premium Gift Box</h4>
                    <p className="text-sm text-gray-600">Included with every order</p>
                  </div>
                </div>
              </div>

              <div className="p-4 border-2 border-gray-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-shield-check-line text-gray-900"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">2-Year Warranty</h4>
                    <p className="text-sm text-gray-600">Included with every order</p>
                  </div>
                </div>
              </div>

              <div className="p-4 border-2 border-gray-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-truck-line text-gray-900"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Free Worldwide Shipping</h4>
                    <p className="text-sm text-gray-600">Insured delivery to your door</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Selected Configuration Summary */}
      {Object.keys(selectedOptions).length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-serif text-xl font-semibold text-gray-900 mb-4">Your Configuration</h3>
          <div className="space-y-3">
            {availableCategories.map(([key]) => {
              if (!selectedOptions[key]) return null;
              return (
                <div key={key} className="flex justify-between text-sm">
                  <span className="text-gray-600">{getCategoryLabel(key)}:</span>
                  <span className="font-medium text-gray-900">{formatValue(selectedOptions[key])}</span>
                </div>
              );
            })}
            {selectedOptions.engraving && (
              <div className="flex justify-between text-sm pt-3 border-t border-gray-200">
                <span className="text-gray-600">Engraving:</span>
                <span className="font-medium text-gray-900">{selectedOptions.engraving}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Price & CTA */}
      <div className="bg-gray-900 text-white rounded-xl p-6 shadow-xl">
        <div className="flex justify-between items-center mb-2">
          <span className="text-lg font-medium">Total Price</span>
          <span className="text-3xl font-bold">${totalPrice.toLocaleString()}</span>
        </div>
        {totalPrice > watchProduct.basePrice && (
          <div className="flex justify-between text-sm text-white/60 mb-4">
            <span>Base: ${watchProduct.basePrice.toLocaleString()}</span>
            <span>Upgrades: +${(totalPrice - watchProduct.basePrice).toLocaleString()}</span>
          </div>
        )}
        <button
          onClick={onAddToCart}
          disabled={!isConfigurationComplete()}
          className={`w-full px-8 py-4 text-base font-semibold rounded-md transition-colors whitespace-nowrap cursor-pointer ${
            isConfigurationComplete()
              ? 'bg-white text-gray-900 hover:bg-gray-100'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isConfigurationComplete() ? 'Add to Cart' : 'Complete All Steps to Add to Cart'}
        </button>
        <p className="text-center text-sm text-white/70 mt-3">Free worldwide shipping on all orders</p>
      </div>
    </div>
  );
}
